import { HistoryLength, type HistoryData, type HistoryEntry } from "@/API";
import {
  getRestaurantCharts,
  restaurantChartsState,
} from "@/store/restaurantChartsState";
import { useEffect, useState, type FC } from "react";
import { Skeleton } from "../ui/skeleton";
import Text from "../Text";
import Badge from "../Badge";
import { useTranslation } from "react-i18next";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";

type Props = {
  restaurantId: string;
};

const getKpiBadge = (value: number, shouldBeFixed = false) => {
  if (value < 0) {
    return (
      <Badge state="error">
        <Text fontSize={12} fontWeight={600} color="#750E13">
          {shouldBeFixed ? value.toFixed(2) : value}
        </Text>
      </Badge>
    );
  }
  if (value > 0) {
    return (
      <Badge state="success">
        <Text fontSize={12} fontWeight={600} color="#044317">
          {`+${shouldBeFixed ? value.toFixed(2) : value}`}
        </Text>
      </Badge>
    );
  }
  return (
    <Badge>
      <Text fontSize={12} fontWeight={600} color="#044317">
        -
      </Text>
    </Badge>
  );
};

const RestaurantEvolution: FC<Props> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { data: restaurantChart, isLoading: restaurantChartIsloading } =
    restaurantChartsState();

  const [length, setLength] = useState<HistoryLength>(HistoryLength.SEVEN_DAYS);

  useEffect(() => {
    getRestaurantCharts({
      restaurantId,
      length,
    });
  }, [length]);

  let kpisEvolution: {
    [key in keyof HistoryData]?: {
      evolution: number;
      currentValue: number;
    };
  } = {};

  if (restaurantChart) {
    Object.entries(restaurantChart).forEach(([key, values]) => {
      if (values.length > 0 && key !== "__typename") {
        const firstValue = values[0] as HistoryEntry | null;
        const lastValue = values[values.length - 1] as HistoryEntry | null;

        if (firstValue !== null && lastValue !== null)
          kpisEvolution = {
            ...kpisEvolution,
            [key]: {
              currentValue: lastValue.value,
              evolution: lastValue.value - firstValue.value,
            },
          };
      }
    });
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-[12px]">
        <Text fontSize={20} fontWeight={600} color="#454F59">
          {t("evolution")}
        </Text>
        <ToggleGroup
          variant="outline"
          type="single"
          value={length}
          onValueChange={(value: HistoryLength) => setLength(value)}
        >
          <ToggleGroupItem value={HistoryLength.SEVEN_DAYS}>
            <Text fontSize={14} fontWeight={600} color="#44546F">
              {t("days_short", { day: 7 })}
            </Text>
          </ToggleGroupItem>
          <ToggleGroupItem value={HistoryLength.FOURTEEN_DAYS}>
            <Text fontSize={14} fontWeight={600} color="#44546F">
              {t("days_short", { day: 14 })}
            </Text>
          </ToggleGroupItem>
          <ToggleGroupItem value={HistoryLength.THIRTY_DAYS}>
            <Text fontSize={14} fontWeight={600} color="#44546F">
              {t("days_short", { day: 30 })}
            </Text>
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      <div className="flex gap-[8px] overflow-y-auto">
        {restaurantChartIsloading ? (
          <>
            <Skeleton className="h-[100px] min-w-[190px] w-[100%]" />
            <Skeleton className="h-[100px] min-w-[190px] w-[100%]" />
            <Skeleton className="h-[100px] min-w-[190px] w-[100%]" />
            <Skeleton className="h-[100px] min-w-[190px] w-[100%]" />
            <Skeleton className="h-[100px] min-w-[190px] w-[100%]" />
          </>
        ) : (
          <>
            {kpisEvolution?.["krunshScore"] && (
              <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-w-[190px] w-[100%]">
                <Text fontSize={12} fontWeight={400}>
                  {t("krunsh_score")}
                </Text>
                <Text fontSize={20} fontWeight={600}>
                  {kpisEvolution?.["krunshScore"].currentValue}
                </Text>
                {getKpiBadge(kpisEvolution?.["krunshScore"].evolution)}
              </div>
            )}
            {kpisEvolution?.["rating"] && (
              <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-w-[190px] w-[100%]">
                <Text fontSize={12} fontWeight={400}>
                  {t("rating")}
                </Text>
                <Text fontSize={20} fontWeight={600}>
                  {kpisEvolution?.["rating"].currentValue}
                </Text>
                {getKpiBadge(kpisEvolution?.["rating"].evolution, true)}
              </div>
            )}
            {kpisEvolution?.["numberOfRatings"] && (
              <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-w-[190px] w-[100%]">
                <Text fontSize={12} fontWeight={400}>
                  {t("rating_count")}
                </Text>
                <Text fontSize={20} fontWeight={600}>
                  {kpisEvolution?.["numberOfRatings"].currentValue}
                </Text>
                {getKpiBadge(kpisEvolution?.["numberOfRatings"].evolution)}
              </div>
            )}
            {kpisEvolution?.["ranking"] && (
              <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-w-[190px] w-[100%]">
                <Text fontSize={12} fontWeight={400}>
                  {t("rank")}
                </Text>
                <Text fontSize={20} fontWeight={600}>
                  {kpisEvolution?.["ranking"].currentValue}
                </Text>
                {getKpiBadge(kpisEvolution?.["ranking"].evolution)}
              </div>
            )}
            {kpisEvolution?.["deliveryTime"] && (
              <div className="bg-[white] rounded-[12px] p-[16px] flex flex-col gap-[8px] min-w-[190px] w-[100%]">
                <Text fontSize={12} fontWeight={400}>
                  {t("delivery_min")}
                </Text>
                <Text fontSize={20} fontWeight={600}>
                  {kpisEvolution?.["deliveryTime"].currentValue}
                </Text>
                {getKpiBadge(kpisEvolution?.["deliveryTime"].evolution)}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
export default RestaurantEvolution;
